/* eslint-disable react-refresh/only-export-components -- provider + companion hook */
import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { themes, type ThemeName, type ThemeColors } from './themes';

export interface ThemeContextType {
  theme: ThemeName;
  colors: ThemeColors;
  toggleTheme: () => void;
  setTheme: (theme: ThemeName) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export interface ThemeProviderProps {
  children: ReactNode;
  /**
   * Default theme to use (defaults to 'elkaBlue')
   */
  defaultTheme?: ThemeName;
  /**
   * localStorage key for persisting theme (defaults to 'elka-theme')
   */
  storageKey?: string;
}

/**
 * Theme provider component
 * Provides theme context to all child components
 */
export function ThemeProvider({
  children,
  defaultTheme = 'elkaBlue',
  storageKey = 'elka-theme',
}: ThemeProviderProps) {
  const [theme, setThemeState] = useState<ThemeName>(() => {
    if (typeof window === 'undefined') {
      return defaultTheme;
    }
    try {
      const saved = localStorage.getItem(storageKey);
      return (saved as ThemeName) || defaultTheme;
    } catch {
      return defaultTheme;
    }
  });

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      localStorage.setItem(storageKey, theme);
      // Set data-theme attribute on body for CSS styling
      document.body.setAttribute('data-theme', theme);
    } catch (error) {
      console.warn('Failed to save theme to localStorage:', error);
    }
  }, [theme, storageKey]);

  const toggleTheme = () => {
    setThemeState((prev) => (prev === 'elkaBlue' ? 'arabGolden' : 'elkaBlue'));
  };

  const setTheme = (newTheme: ThemeName) => {
    setThemeState(newTheme);
  };

  return (
    <ThemeContext.Provider value={{ theme, colors: themes[theme], toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

/**
 * Hook to access theme context
 * @returns Theme state and methods
 */
export function useTheme(): ThemeContextType {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
